import { useMatchmakingState } from './useMatchmakingState';
import { useClientManagement } from './useClientManagement';
import { useLoadingMessages } from './useLoadingMessages';
import { useImagePreloading } from './useImagePreloading';
import { useAddressGeocoding } from './useAddressGeocoding';
import { useMatchmakingComputed, useHistoryFetchEffect } from './useMatchmakingComputed';
import { useAddressHandling } from './useAddressHandling';
import { useCreateSearch, useFilteredProviders, useSaveSearchResults } from './useMatchmaking';
import { useHistorySearch } from './useHistorySearch';
import { useMatchSearch } from './useMatchSearch';
import { useLoadMore } from './useLoadMore';
type UseMatchmakingDataParams = {
  state: ReturnType<typeof import('./useMatchmakingStateManagement').useMatchmakingStateManagement>;
};

export const useMatchmakingData = ({ state }: UseMatchmakingDataParams) => {
  const {
    isLoading,
    setIsLoading,
    isLoadingMore: _isLoadingMore,
    setIsLoadingMore,
    error: _error,
    setError,
    showNoProvidersFound: _showNoProvidersFound,
    setShowNoProvidersFound,
    noProviderMessage: _noProviderMessage,
    setNoProviderMessage,
    loadMoreMessage: _loadMoreMessage,
    setLoadMoreMessage,
    searchCounter,
    setSearchCounter,
    isHistoryPopoverOpen: _isHistoryPopoverOpen,
    isLocationModalOpen,
    setIsLocationModalOpen,
    isViewingHistoryResults,
    setIsViewingHistoryResults,
    shouldFetchHistory: _shouldFetchHistory,
    setShouldFetchHistory,
    currentSearchId,
    setCurrentSearchId,
    loadCount,
    setLoadCount,
    selectedAddress,
    setSelectedAddress,
    selectedLatitude,
    setSelectedLatitude,
    selectedLongitude,
    setSelectedLongitude,
    selectedState,
    setSelectedState,
    selectedCounty,
    setSelectedCounty,
    selectedZipCode,
    setSelectedZipCode,
    stepperAnswers,
    lastHistoryItemRef,
    lastPromptRef,
  } = state;

  const {
    clients,
    clientsError,
    clientsLoading,
    selectedClientId: selectedClientIdValue,
    setSelectedClientId,
    advisorProfileId,
    selectedClient,
    hasClients,
  } = useClientManagement();

  const { currentLoadingMessageIndex, currentLoadingTheme } = useLoadingMessages(isLoading);

  const matchmakingState = useMatchmakingState(selectedClientIdValue);
  const matchingPrompt = matchmakingState.query;
  const apiResults = matchmakingState.matches;
  const hasMore = matchmakingState.hasMore;

  const {
    isPreloadingImages,
    preloadedImages,
    setPreloadedImages: setPreloadedImagesValue,
    setIsPreloadingImages: setIsPreloadingImagesValue,
    imagePreloadingMessageIndex,
    currentImagePreloadingTheme,
  } = useImagePreloading(apiResults, isLoading);


  const { userClearedAddressRef } = useAddressGeocoding({
    isLocationModalOpen,
    selectedClient,
    selectedAddress,
    selectedLatitude,
    selectedLongitude,
    selectedState,
    selectedCounty,
    selectedZipCode,
    setSelectedAddress,
    setSelectedLatitude,
    setSelectedLongitude,
    setSelectedState,
    setSelectedCounty,
    setSelectedZipCode,
  });

  const { handleAddressSelectAndGeocode } = useAddressHandling({
    userClearedAddressRef,
    setSelectedAddress,
    setSelectedLatitude,
    setSelectedLongitude,
    setSelectedState,
    setSelectedCounty,
    setSelectedZipCode,
  });

  const {
    clientProfile,
    ageDisplay,
    matchResults,
    hasMatches,
    shouldShowPostSingleLoadExhaustedMessage,
    shouldShowPostSecondLoadMessage,
    cheerfulMessage,
  } = useMatchmakingComputed({
    selectedClient,
    selectedZipCode,
    selectedCounty,
    isViewingHistoryResults,
    searchCounter,
    isLoading,
    hasMore,
    loadCount,
    apiResults,
  });

  // Trigger history fetch when advisor or client changes
  // This ensures history is fetched on initial load and when switching clients
  useHistoryFetchEffect(advisorProfileId, selectedClientIdValue, setShouldFetchHistory);

  const createSearchMutation = useCreateSearch();
  const filteredProvidersMutation = useFilteredProviders();
  const saveResultsMutation = useSaveSearchResults();

  const { fetchHistoryResults } = useHistorySearch({
    advisorProfileId,
    selectedClientId: selectedClientIdValue,
    loadCount,
    matchmakingState,
    preloadedImages,
    setIsPreloadingImages: setIsPreloadingImagesValue,
    setPreloadedImages: setPreloadedImagesValue,
    setShowNoProvidersFound,
    setNoProviderMessage,
    setError,
    setIsViewingHistoryResults,
    setCurrentSearchId,
    setLoadCount,
    setSearchCounter,
    lastPromptRef,
    lastHistoryItemRef,
    setIsLoading,
    setIsLoadingMore,
  });

  const { executeMatchSearch } = useMatchSearch({
    matchingPrompt,
    hasClients,
    selectedClientId: selectedClientIdValue,
    advisorProfileId,
    stepperAnswers,
    apiResults,
    currentSearchId,
    loadCount,
    selectedState,
    selectedCounty,
    selectedAddress,
    selectedZipCode,
    selectedLatitude,
    selectedLongitude,
    clientProfile,
    matchmakingState,
    isViewingHistoryResults,
    createSearchMutation,
    filteredProvidersMutation,
    saveResultsMutation,
    setIsLoading,
    setIsLoadingMore,
    setError,
    setShowNoProvidersFound,
    setNoProviderMessage,
    setLoadMoreMessage,
    setIsLocationModalOpen,
    setCurrentSearchId,
    setLoadCount,
    setSearchCounter,
    setIsViewingHistoryResults,
    setShouldFetchHistory,
    lastPromptRef,
  });

  const { handleLoadMore } = useLoadMore({
    matchingPrompt,
    hasClients,
    selectedClientId: selectedClientIdValue,
    currentSearchId,
    advisorProfileId,
    loadCount,
    stepperAnswers,
    apiResults,
    matchmakingState,
    preloadedImages,
    filteredProvidersMutation,
    saveResultsMutation,
    setIsLoadingMore,
    setError,
    setLoadMoreMessage,
    setLoadCount,
    setIsPreloadingImages: setIsPreloadingImagesValue,
    setPreloadedImages: setPreloadedImagesValue,
  });

  return {
    clients,
    clientsError,
    clientsLoading,
    selectedClientId: selectedClientIdValue,
    setSelectedClientId,
    advisorProfileId,
    selectedClient,
    hasClients,
    currentLoadingMessageIndex,
    currentLoadingTheme,
    matchmakingState,
    matchingPrompt,
    apiResults,
    hasMore,
    isPreloadingImages,
    preloadedImages,
    setPreloadedImages: setPreloadedImagesValue,
    setIsPreloadingImages: setIsPreloadingImagesValue,
    imagePreloadingMessageIndex,
    currentImagePreloadingTheme,
    userClearedAddressRef,
    handleAddressSelectAndGeocode,
    clientProfile,
    ageDisplay,
    matchResults,
    hasMatches,
    shouldShowPostSingleLoadExhaustedMessage,
    shouldShowPostSecondLoadMessage,
    cheerfulMessage,
    fetchHistoryResults,
    executeMatchSearch,
    handleLoadMore,
  };
};

